import React, { useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import styles from './DataAiForm.module.scss';
import TextEditor from 'component/atom/forms/text-editor/TextEditor';
import Select from 'component/atom/forms/select/select/Select';
import TextArea from 'component/atom/forms/text-area/TextArea';
import { useFetch } from 'hooks/useFetch';
import { Agent, variableManagement, workflow } from 'hooks/endpoints';
import { useParams } from 'react-router-dom';
import MultiSelectList from 'component/atom/forms/select/multi-select-list/MultiSelectList';

const DataAiForm = ({ workflowData }) => {
  const formik = useFormikContext();
  const { id } = useParams();
  const [themesOptions, setThemesOptions] = useState([]);

  const { data, isFetching } = useFetch(
    ['variableManagement'],
    variableManagement.get,
    { enabled: true },
    { pageSize: 1000, pageNumber: 1 }
  );
  const { data: agentTypesData } = useFetch(
    ['allAgentsTypes'],
    Agent.getAgentsTypes,
    {
      enable: true,
    }
  );
  const { data: themes, isFetching: isLoading } = useFetch(
    ['themesByWorkflowId'],
    workflow.getThemesByWorkFlowId(id)
  );
  const {
    data: connections,
    refetch: refetchConnections,
    isFetching: isLoadingConnections,
  } = useFetch(
    ['connectionsByBotId', workflowData?.botId],
    Agent.getConnectionByBotId(workflowData?.botId, formik.values.agentType),
    {
      enabled: false,
    }
  );
  const {
    data: apis,
    refetch: refetchApis,
    isFetching: isFetchingApis,
  } = useFetch(
    ['connectionApis', workflowData?.botId],
    Agent.getApis(formik.values.agentId),
    {
      enabled: false,
    }
  );

  useEffect(() => {
    if (data?.items) {
      const selectedVariable = data.items.find(
        (item) => item.variableId == formik.values.variableId
      )?.variableName;
      formik.setFieldValue('variableName', selectedVariable || '');
    }
  }, [formik.values.variableId, data]);

  const typeOptions = data?.data?.items?.map((item) => ({
    value: item.variableId,
    label: item.variableName,
  }));

  const agentOptions = agentTypesData?.data?.map((val) => ({
    label: val.agentName,
    value: val.id,
  }));

  const connectionsOptions = connections?.data?.map((item) => ({
    value: item.id,
    label: item.agentName,
  }));

  const apiOptions = apis?.data?.map((item) => ({
    value: item.id,
    label: item.apiName,
  }));

  useEffect(() => {
    setThemesOptions([
      {
        value: 1,
        label: 'Default',
      },
      ...(Array.isArray(themes?.data?.items)
        ? themes.data.items.map((item) => ({
            value: item.value,
            label: item.label,
          }))
        : []),
    ]);
  }, [themes]);

  useEffect(() => {
    if (undefined !== formik.values.agentType) {
      refetchConnections();
    }
  }, [formik.values.agentType]);

  useEffect(() => {
    if (undefined !== formik.values.agentId) {
      refetchApis();
    }
  }, [formik.values.agentId]);

  const handleMultiSelectChange = (event) => {
    const selectedValues = event.target.value;
    formik.setFieldValue('tags', selectedValues.join(', '));
  };

  return (
    <form
      className={styles['response-ai-form']}
      onSubmit={formik.handleSubmit}
      onKeyDown={(e) => {
        // prevent default form submission on Enter if needed
      }}
    >
      <TextEditor
        name='question'
        label='Question'
        value={formik.values.question}
        onChange={(value) => formik.setFieldValue('question', value)}
        onBlur={formik.handleBlur}
        error={formik.errors.question}
        touched={formik.touched.question}
        isToolbarReduced
        isCustom
        className={styles.editor}
        textEditorClassName={styles['editor-class']}
      />

      <Select
        label='System Type'
        placeholder='Select system type'
        options={agentOptions}
        name='agentType'
        value={formik.values.agentType}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.errors.agentType}
        touched={formik.touched.agentType}
        classname={styles['daily-select']}
        selectClassname={styles.select}
        optionClassname={styles.whiteOptions}
        optionsClassName={styles.options}
      />
      {formik.values.agentType && (
        <Select
          label='Agents'
          placeholder='Select Agent'
          options={connectionsOptions}
          name='agentId'
          value={formik.values.agentId}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.agentId}
          touched={formik.touched.agentId}
          classname={styles['daily-select']}
          selectClassname={styles.select}
          optionClassname={styles.whiteOptions}
          optionsClassName={styles.options}
          isLoading={isLoadingConnections}
        />
      )}
      {formik.values.agentId && (
        <MultiSelectList
          label='API'
          name='endpoints'
          placeholder='Select'
          value={formik.values.endpoints}
          options={apiOptions}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.endpoints}
          touched={formik.touched.endpoints}
          isLoading={isLoadingConnections}
        />
      )}

      <TextArea
        label='Prompt Description'
        name='promptDescription'
        placeholder='Enter Description'
        value={formik.values.promptDescription}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.errors.promptDescription}
        touched={formik.touched.promptDescription}
        isExpanded
        cols='50'
        minHeight='50vh'
      />
      <Select
        label='Theme'
        name='responseThemeId'
        placeholder='Select'
        value={formik.values.responseThemeId}
        options={themesOptions}
        onChange={formik.handleChange}
        error={formik.errors.responseThemeId}
        touched={formik.touched.responseThemeId}
        isLoading={isLoading}
      />
      <Select
        label='Output Variable'
        name='outputVariableId'
        placeholder='Select Output Variable'
        value={formik.values.outputVariableId}
        options={typeOptions}
        onChange={formik.handleChange}
        error={formik.errors.outputVariableId}
        touched={formik.touched.outputVariableId}
        isLoading={isFetching}
      />
    </form>
  );
};

export default DataAiForm;
